/**
 * Manufacturing Access Wrapper Component
 *
 * Checks if user has manufacturing unit access before rendering the ManufacturingModule.
 * This wrapper properly handles the access check without breaking React hooks rules.
 *
 * Follows the same pattern as GaushalaAccessWrapper.
 */

import React, { useEffect, useState } from 'react';
import ManufacturingModule from './ManufacturingModule.container';
import NoUnitAssigned from '@/components/access/NoUnitAssigned';
import { getManufacturingProfile, ManufacturingError } from '@/services/manufacturing/profileService';
import { useAuth } from '@/contexts/AuthContext';
import { Factory } from 'lucide-react';

type AccessState = 'checking' | 'allowed' | 'denied';

/**
 * Check if user has ADMIN role from their stored user data
 */
const isAdminUser = (user: any): boolean => {
  if (!user) return false;
  const roles = user.roles || [];
  const role = user.role || '';
  return roles.includes('ADMIN') || role === 'ADMIN';
};

/**
 * ManufacturingAccessWrapper Component
 * Renders the appropriate component based on manufacturing unit access status
 */
const ManufacturingAccessWrapper: React.FC = () => {
  const [accessState, setAccessState] = useState<AccessState>('checking');
  const { user } = useAuth();

  // Check manufacturing access on mount
  useEffect(() => {
    const checkAccess = async () => {
      // ADMIN users always have access - they see aggregate data across all units
      if (isAdminUser(user)) {
        setAccessState('allowed');
        return;
      }

      try {
        await getManufacturingProfile();
        setAccessState('allowed');
      } catch (error) {
        // Fail closed: anything other than a successful profile check denies access.
        // The old "allow on unknown error" fallback let users through the wrapper
        // on 403/5xx and they'd see a broken dashboard with all API calls failing.
        if (error instanceof ManufacturingError && error.code === 'NO_UNIT_ASSIGNED') {
          setAccessState('denied');
        } else {
          console.error('Manufacturing access check failed, denying access:', error);
          setAccessState('denied');
        }
      }
    };

    checkAccess();
  }, [user]);

  // Show loading spinner
  if (accessState === 'checking') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Manufacturing...</p>
        </div>
      </div>
    );
  }

  // Show no unit page if denied
  if (accessState === 'denied') {
    return (
      <NoUnitAssigned
        moduleName="Manufacturing Unit"
        icon={Factory}
        theme={{
          gradient: 'from-orange-50 via-amber-50 to-yellow-50',
          cardBorder: 'border-orange-200',
          iconBg: 'bg-orange-100',
          iconColor: 'text-orange-600',
        }}
      />
    );
  }

  // Show main manufacturing module if allowed
  return <ManufacturingModule />;
};

export default ManufacturingAccessWrapper;
